import Zone from "@insite/client-framework/Components/Zone";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import LazyImage from "@insite/mobius/LazyImage";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import submitContactFormWithProduct from "@thkStore/Store/Components/ContactUsForm/Handlers/SubmitContactFormWithProduct";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";

// Re-export everything from the original
export * from "@insite/content-library/Widgets/ContactUs/ContactForm";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session?.isAuthenticated,
    isSessionLoaded: state.context.session?.userProfileId,
});

const mapDispatchToProps = {
    submitContactFormWithProduct,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
}

export const contactFormStyles: ContactFormStyles = {
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            float: right;
            margin-top: 20px;
            border-width: 1px;

            &:hover{
                color: ${({ theme }) => theme.colors.primary.contrast};

                svg{
                    stroke: ${({ theme }) => theme.colors.primary.contrast};
                }
            }
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
};

const styles = contactFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
    // Lets the Attachment widget hand the selected files back to the form on submit.
    getAttachments?: () => File[];
    // Lets the form reset the Attachment widget's local file list after a successful submit.
    clearAttachments?: () => void;
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const getIconConfig = () => {
    return { src: "ChevronRight", position: "right" as const };
};

const productCardStyles = {
    container: {
        gap: 0,
        css: css`
            padding: 20px 0;
            border-top: 1px solid #e0e0e0;
            margin-top: 20px;
        `,
    } as GridContainerProps,
    imageGridItem: {
        width: [4, 4, 3, 3, 2],
        css: css`
            padding-right: 20px;
            justify-content: center;
        `,
    } as GridItemProps,
    infoGridItem: {
        width: [8, 8, 9, 9, 10],
    } as GridItemProps,
    infoContainer: {
        gap: 5,
        css: css`
            width: 100%;
        `,
    } as GridContainerProps,
    titleGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        `,
    } as GridItemProps,
    partNumbersGridItem: { width: 12 } as GridItemProps,
};

const CmsContactForm = ({
    fields, submitContactFormWithProduct, clearForm, validateReCaptcha, id,
    isAuthenticated, isSessionLoaded,
}: Props) => {
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const contextValue = React.useRef<ContextType>({ validators });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [product, setProduct] = useState<ProductModel | null>(null);
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const recaptchaRef = React.useRef<ReCAPTCHA>(null);
    const toasterContext = React.useContext(ToasterContext);

    const { emailRecipients, submitButtonText, successMessage } = fields;

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const { productId } = parseQueryStringCaseInsensitive(window.location.search, { productId: "" as string | undefined });
        if (!productId) {
            return;
        }

        setIsLoadingProduct(true);
        getProductById({ id: productId, expand: ["images"] })
            .then(result => {
                setProduct(result);
            })
            .catch(() => {
                setProduct(null);
            })
            .finally(() => {
                setIsLoadingProduct(false);
            });
    }, []);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let isFormValid = true;
        for (const validator of Object.values(validators)) {
            if (validator && !validator()) {
                isFormValid = false;
            }
        }

        if (!isFormValid) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);

        const productInfo = product
            ? {
                  productId: product.id,
                  productName: product.productTitle,
                  productNumber: product.productNumber,
                  productUrl: `${window.location.origin}${product.canonicalUrl}`,
                  productImage: product.mediumImagePath || product.smallImagePath || "",
              }
            : undefined;

        const attachments = contextValue.current.getAttachments?.() ?? [];

        submitContactFormWithProduct({
            emailRecipients,
            product: productInfo,
            attachments,
            onSuccess: () => {
                setIsModalOpen(true);
                setIsSubmitting(false);
            },
            onException: (error: Error) => {
                toasterContext.addToast({
                    body: error.message || (siteMessage("ContactUsForm_SubmitGenericErrorMessage") as string),
                    messageType: "danger",
                });
                setIsSubmitting(false);
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
        clearForm();
        contextValue.current.clearAttachments?.();
    };

    const productImageSrc = product?.mediumImagePath || product?.smallImagePath || "";

    return (
        <LoadingOverlay loading={isLoadingProduct || isSubmitting || (isAuthenticated === true && !isSessionLoaded)}>
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <ContactFormContext.Provider value={contextValue.current}>
                    <form id="contactForm" onSubmit={handleFormSubmit} noValidate>
                        <Zone contentId={id} zoneName="Content" />
                        {product && (
                            <GridContainer {...productCardStyles.container} data-test-selector="contactUsProductCard">
                                <GridItem {...productCardStyles.imageGridItem}>
                                    <LazyImage
                                        src={productImageSrc}
                                        altText={product.imageAltText || product.productTitle}
                                        css={css`
                                            img {
                                                max-width: 100%;
                                                max-height: 140px;
                                                object-fit: contain;
                                            }
                                        `}
                                    />
                                </GridItem>
                                <GridItem {...productCardStyles.infoGridItem}>
                                    <GridContainer {...productCardStyles.infoContainer}>
                                        <GridItem {...productCardStyles.titleGridItem}>
                                            <Typography size={20} weight="bold">
                                                {product.productTitle}
                                            </Typography>
                                            <Clickable
                                                onClick={() => {
                                                    setProduct(null);
                                                    const url = new URL(window.location.href);
                                                    url.searchParams.delete("productId");
                                                    url.searchParams.delete("productid");
                                                    window.history.replaceState({}, "", url.toString());
                                                }}
                                                data-test-selector="contactUsRemoveProduct"
                                                css={css`
                                                    flex-shrink: 0;
                                                    margin-left: 8px;
                                                    padding: 4px;
                                                    &:hover { opacity: 0.7; }
                                                `}
                                            >
                                                <Icon src={XCircle} size={20} />
                                            </Clickable>
                                        </GridItem>
                                        <GridItem {...productCardStyles.partNumbersGridItem}>
                                            <GridContainer gap={5}>
                                                {product.productNumber && (
                                                    <GridItem width={12} css={css` display: flex; gap: 4px; `}>
                                                        <Typography weight="bold">{translate("Part #")}</Typography>
                                                        <Typography>{product.productNumber}</Typography>
                                                    </GridItem>
                                                )}
                                                {product.manufacturerItem && (
                                                    <GridItem width={12} css={css` display: flex; gap: 4px; `}>
                                                        <Typography weight="bold">{translate("MFG #")}</Typography>
                                                        <Typography>{product.manufacturerItem}</Typography>
                                                    </GridItem>
                                                )}
                                            </GridContainer>
                                        </GridItem>
                                        {product.brand?.name && (
                                            <GridItem width={12}>
                                                <Typography size={13} color="text.accent">
                                                    {product.brand.name}
                                                </Typography>
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        )}
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                        <Button
                            {...styles.submitButton}
                            variant="primary"
                            icon={getIconConfig()}
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {submitButtonText}
                        </Button>
                        <Modal
                            headline={translate("Message Sent")}
                            contentLabel={successMessage}
                            {...styles.successModal}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer {...styles.modalContainer}>
                                <GridItem {...styles.messageTextGridItem}>
                                    <Typography {...styles.messageText}>{successMessage}</Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button {...styles.continueButton} onClick={modalCloseHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                    </form>
                </ContactFormContext.Provider>
            </GridItem>
        </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CmsContactForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                validate: (value: string) => {
                    if (!value) {
                        return "Email recipients is required";
                    }

                    return emailsRegexp.test(value.trim()) ? "" : "Enter valid email address(es)";
                },
                tooltip: "Email addresses separated by commas",
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
            },
            {
                name: fields.successMessage,
                displayName: "Success Message",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for contacting us. You should receive a response within 48 hours.",
            },
        ],
    },
};

export default widgetModule;
